/**
 * Pre-Consent Panel for Bank Connection
 * 
 * Displays clear information about what happens when connecting a bank
 * before redirecting to Akahu OAuth flow.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import {
  Building2,
  Shield,
  Eye,
  KeyRound,
  Unlink,
  ExternalLink,
  CheckCircle2,
  ArrowRight,
  Lock,
  Info
} from 'lucide-react';

export default function BankConnectionConsentPanel({ 
  open, 
  onOpenChange, 
  onConfirm, 
  loading = false 
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="bank-consent-dialog">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center">
              <Building2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <DialogTitle className="text-xl">Connect Your Bank</DialogTitle>
              <DialogDescription className="text-sm">
                Securely link your accounts via Akahu
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* What we'll do with your data */}
        <div className="space-y-4 py-4">
          <div className="p-4 bg-muted/30 rounded-lg">
            <h4 className="font-medium text-foreground mb-3 flex items-center gap-2">
              <Info className="w-4 h-4 text-[#1a4d3e]" />
              What happens when you connect
            </h4>
            <p className="text-sm text-muted-foreground leading-relaxed">
              ARA Wealth will retrieve your account balances and transaction history to help you 
              understand and organise your household finances. We use this data to power your 
              Review & reset, cash flow forecasts, and spending insights.
            </p>
          </div>

          {/* Key points */}
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center flex-shrink-0">
                <Shield className="w-4 h-4 text-teal-600 dark:text-teal-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">Powered by Akahu</p>
                <p className="text-xs text-muted-foreground">
                  Your connection is handled by Akahu, a licensed NZ open banking provider.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                <Eye className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">Read-only access</p>
                <p className="text-xs text-muted-foreground">
                  We can only view your data. We cannot move money or make transactions.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center flex-shrink-0">
                <KeyRound className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">Your passwords stay private</p>
                <p className="text-xs text-muted-foreground">
                  ARA Wealth never sees your bank login credentials. You authenticate directly with your bank.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center flex-shrink-0">
                <Unlink className="w-4 h-4 text-slate-600 dark:text-slate-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">Disconnect anytime</p>
                <p className="text-xs text-muted-foreground">
                  You can disconnect your bank from within ARA Wealth whenever you choose.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-3 pt-2">
          <Button
            onClick={onConfirm}
            disabled={loading}
            className="w-full bg-teal-600 hover:bg-teal-700 text-white"
            data-testid="confirm-connect-bank-btn"
          >
            {loading ? (
              <>Connecting...</>
            ) : (
              <>
                Continue to Akahu
                <ArrowRight className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>
          
          <Link 
            to="/security/open-banking" 
            className="flex items-center justify-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
            data-testid="learn-more-link"
          >
            <Lock className="w-3 h-3" />
            Learn more about how we protect your data
            <ExternalLink className="w-3 h-3" />
          </Link>
        </div>
      </DialogContent>
    </Dialog>
  );
}
